var FitToScrollerSupport     = require("enyo-ui/FitToScrollerSupport"),
    RepeaterArrayDataAdapter = require("enyo-ui/RepeaterArrayDataAdapter"),
    RepeaterDrawLoopSupport  = require("enyo-ui/RepeaterDrawLoopSupport"),
    ResizeSensorSupport      = require("enyo-ui/ResizeSensorSupport"),
    ScrollRepeater           = require("enyo-ui/ScrollRepeater"),
    kind                     = require("enyo/kind");

require("app/ui/repeaters/PrefixedScrollRepeater.less");

/**
 * [PrefixedScrollRepeater]{@link app.ui.repeaters.PrefixedScrollRepeater}
 * displays a vertically scrolling repeater with the ability to prefix some
 * content above the grid yet *inside* the scroller.
 *
 * For performance reasons, this component will re-evaluate the height of the
 * prefixed content on reflow only.
 *
 * @ui
 * @class    app.ui.repeaters.PrefixedScrollRepeater
 * @extends  enyo-ui.ScrollRepeater
 * @mixes    enyo-ui.RepeaterDrawLoopSupport
 * @public
 */
module.exports = kind(/** @lends  app.ui.repeaters.PrefixedScrollRepeater.prototype */ {

    /**
     * @private
     */
    name: "app.ui.repeaters.PrefixedScrollRepeater",

    /**
     * @private
     */
    kind: ScrollRepeater,

    /**
     * @private
     */
    classes: "app-ui-repeaters-prefixed-scroll-repeater",

    /**
     * @private
     */
    fit: true,

    /**
     * @private
     */
    mixins: [
        RepeaterDrawLoopSupport
    ],

    /**
     * @type     {number}
     * @default  0
     * @public
     */
    prefixHeight: 0,

    /**
     * Simple array support, refreshes the entire view when data changes.
     *
     * @type {enyo-ui.RepeaterArrayDataAdapter}
     * @public
     */
    dataAdapterKind: RepeaterArrayDataAdapter,

    /**
     * Extra options to mix into the scroller instance.
     *
     * @type  {Object=}
     * @public
     */
    scrollerOptions: {
        vertical:       true,
        horizontal:     false,
        scrollChaining: false
    },

    /**
     * @private
     */
    components: [{
        name:   "client",
        mixins: [
            FitToScrollerSupport,
            ResizeSensorSupport
        ],
        classes:      "app-ui-repeaters-prefixed-scroll-repeater__prefix",
        scrollerPath: ".owner.$.scroller"
    }, {
        name:    "childContainer",
        classes: "app-ui-repeaters-prefixed-scroll-repeater__child-container"
    }],

    /**
     * @private
     */
    handlers: {
        onNodeResized: "onNodeResized"
    },

    /**
     * Respond to resize events.
     *
     * @param    {enyo.Control}  sender  The sender of the event
     * @param    {Object}        event   The event object.
     * @returns  {boolean}       No further bubbling needed.
     * @private
     */
    onNodeResized: function(sender, event) {
        if (event.originator === this.$.client) {
            this.set("prefixHeight", event.height);
        }

        return true;
    },

    /**
     * @param  {number}  oldHeight  The previous height value
     * @private
     */
    prefixHeightChanged: function(oldHeight) {
        if (oldHeight !== this.prefixHeight) {
            this.snapToAnchor();
            this.fit && this.scaleChildContainer();
        }
    },

    /**
     * Scales the childContainer to accommodate the entire arrangement.
     *
     * @protected
     */
    scaleChildContainer: function() {
        var childContainer  = this.getChildContainer(),
            arrangementSize = this.arrangementSize,
            repeaterSize    = this.getBounds(),
            bounds          = {},
            prefixHeight    = this.get("prefixHeight"),
            childContainerBounds;

        // Scaling the childContainer happens during reflow, but the repeater
        // children are redrawn later for performance reasons. Because of this,
        // child controls might extend outside of the boundaries of the
        // childContainer until they have been re-drawn.
        //
        // In order to ensure the dimensions we apply here are reliable we
        // cannot allow the childContainer to have any visible overflow.
        childContainer.applyStyle("overflow", "hidden");

        if (arrangementSize.width || arrangementSize.width === 0) {
            bounds.width = arrangementSize.width;
        }

        if (arrangementSize.height || arrangementSize.height === 0) {
            bounds.height = arrangementSize.height;
        }

        // Expand height to fill the repeater if needed
        if (this.fit) {
            bounds.height = Math.max(repeaterSize.height - prefixHeight, bounds.height || 0);
        }

        // Apply new bounds if changed
        if (bounds.width !== this.childContainerSize.width
            || bounds.height !== this.childContainerSize.height) {
            childContainer.setBounds(bounds);
            childContainerBounds = childContainer.getBounds();
            this.set("childContainerSize", {
                width:  childContainerBounds.width,
                height: childContainerBounds.height
            });
        }
    },

    /**
     * Gets the bounds of the area for which children need to be arranged. The
     * bounds should be defined in pixels, relative to the top-left corner of
     * the childContainer.
     *
     * Override this method if the top-left corner of the childContainer does
     * not align with the top-left corner of the scroller.
     *
     * @return  {Object}  The bounds
     * @public
     */
    getArrangementBounds: kind.inherit(function(sup) {
        return function() {
            var prefixHeight = this.prefixHeight || 0,
                bounds;

            bounds        = sup.apply(this, arguments);
            bounds.top    = Math.max(0, bounds.top - prefixHeight);
            bounds.bottom = Math.max(0, bounds.bottom - prefixHeight);

            return bounds;
        };
    }),

    /**
     * Figures out which index is currently located closest to the top-left
     * corner of the viewport as well as it's offset relative to the top-left
     * corner of the viewport.
     *
     * By default we assume the top-left corner of the childContainer aligns
     * with the top-lef corner of the scroller. Override this method if this is
     * not the case.
     *
     * @public
     */
    updateAnchor: function() {
        var scrollPosition = this.getScroller().get("scrollPosition"),
            index,
            offsetLeft,
            offsetTop,
            prefixHeight   = this.prefixHeight;

        index      = this.arranger.getNearestIndex(scrollPosition.scrollLeft, scrollPosition.scrollTop - prefixHeight);
        offsetLeft = scrollPosition.scrollLeft - this.arranger.getScrollLeft(index);
        offsetTop  = scrollPosition.scrollTop - prefixHeight - this.arranger.getScrollTop(index);

        if (index === 0 && offsetTop < 0) {
            index     -= 1;
            offsetTop = scrollPosition.scrollTop;
        }

        this.set("anchor", {
            index:      index,
            offsetLeft: offsetLeft,
            offsetTop:  offsetTop
        });
    },

    /**
     * Snaps the repeater to the current anchor.
     *
     * By default we assume the top-left corner of the childContainer aligns
     * with the top-lef corner of the scroller. Override this method if this is
     * not the case.
     *
     * @public
     */
    snapToAnchor: function() {
        var scroller       = this.getScroller(),
            arranger       = this.arranger,
            anchor         = this.anchor,
            prefixHeight   = this.prefixHeight,
            length         = this.get("length"),
            index,
            scrollPosition = scroller.scrollPosition,
            scrollLeft,
            scrollTop;

        // Normalize index before use.
        index = anchor.index > length - 1 ? length - 1 : anchor.index;

        if (!scroller.scrolling) {
            if (index >= 0) {
                scrollLeft = arranger.getScrollLeft(index) + anchor.offsetLeft;
                scrollTop  = arranger.getScrollTop(index) + anchor.offsetTop + prefixHeight;
            }
            else {
                scrollLeft = anchor.offsetLeft;
                scrollTop  = anchor.offsetTop;
            }

            if (scrollPosition.scrollLeft !== scrollLeft
                || scrollPosition.scrollTop !== scrollTop) {
                this.snappingToAnchor = true;
                scroller.snapTo(scrollLeft, scrollTop);
            }
        }
    },

    /**
     * Scrolls the repeater to the provided index.
     *
     * By default we assume the top-left corner of the childContainer aligns
     * with the top-lef corner of the scroller. Override this method if this is
     * not the case.
     *
     * @param {number}  index  The index
     * @public
     */
    scrollToIndex: function(index) {
        var scroller = this.getScroller(),
            arranger = this.arranger;

        // Normalize index
        index = index > length - 1 ? length - 1 : index;

        scroller.scrollTo(
            arranger.getScrollLeft(index),
            arranger.getScrollTop(index) + (index >= 0 ? this.prefixHeight : 0)
        );
    },

    /**
     * Snaps the repeater to the provided index.
     *
     * By default we assume the top-left corner of the childContainer aligns
     * with the top-lef corner of the scroller. Override this method if this is
     * not the case.
     *
     * @param {number}  index  The index
     * @public
     */
    snapToIndex: function(index) {
        var scroller = this.getScroller(),
            arranger = this.arranger;

        // Normalize index
        index = index > length - 1 ? length - 1 : index;

        scroller.snapTo(
            arranger.getScrollLeft(index),
            arranger.getScrollTop(index) + (index >= 0 ? this.prefixHeight : 0)
        );
    }

});
